import ModalHeader from '@molecules/ModalHeader';
import ModalWindow from '@organisms/modals/ModalWindow';
import {FC, useMemo, useState} from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import {ButtonsWrapper, ModalContent, ModalWrapper} from './EditAccessRightsModal.styles';
import Button from '@atoms/Button';
import Text from '@atoms/Text';
import {systems} from '@store/slices/systems';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router';
import {
  CalendarWrapper,
  CalendarHeadersRow,
  CalendarHeaderCell,
  SelectedDateInfo,
} from './AddSystemPlanModal.styles';
import {CalendarNavButton} from '@molecules/SelectDateInput.styles';
import {ArrowLeft, ArrowRight} from '@carbon/icons-react';
import {theme} from '@styles/theme';
import {addMonths, formatMonth, formatNonTodayFull} from '@utils/common';
import {selectCoveragePlans} from '@store/slices/coveragePlans';
import {toISODateString} from '@utils/common';
import {useNavigate} from 'react-router';
import {ISystem} from '@types';

interface Props {
  isOpened: boolean;
  onCloseRequest: () => void;
}

const AddSystemPlanModal: FC<Props> = ({isOpened, onCloseRequest}) => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const systemsArr: ISystem[] = useSelector(systems);
  const currentSystem = systemsArr.find(system => String(system.id) === id);
  const plansForSystem = useSelector(
    selectCoveragePlans
  );
  const plannedDateSet = useMemo(
    () => new Set(plansForSystem.map(p => p.date)),
    [plansForSystem],
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [activeStartDate, setActiveStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  return (
    <ModalWindow isOpen={isOpened} overflowIsVisible>
      <ModalWrapper>
        <ModalHeader title={'Добавить план покрытия'} onClose={onCloseRequest} />
        <ModalContent>
          <Text text={currentSystem?.name || ''} type="captionB" />
          <CalendarWrapper>
            <CalendarHeadersRow>
              <CalendarNavButton
                onClick={() => setActiveStartDate(addMonths(activeStartDate, -1))}>
                <ArrowLeft width={16} fill={theme.colors.accent00} />
              </CalendarNavButton>
              <CalendarHeaderCell>{formatMonth(activeStartDate)}</CalendarHeaderCell>
              <CalendarNavButton
                onClick={() => setActiveStartDate(addMonths(activeStartDate, 1))}>
                <ArrowRight size={16} fill={theme.colors.accent00} />
              </CalendarNavButton>
            </CalendarHeadersRow>
            <Calendar
              onChange={value => {
                const next = (value as Date) || null;
                setSelectedDate(next);
              }}
              value={selectedDate}
              selectRange={false}
              locale="ru-RU"
              showNeighboringMonth={false}
              minDetail="month"
              prev2Label={null}
              next2Label={null}
              showFixedNumberOfWeeks={false}
              activeStartDate={activeStartDate}
              onActiveStartDateChange={({activeStartDate}) =>
                setActiveStartDate(activeStartDate || new Date())
              }
              tileDisabled={({date, view}) => {
                if (view !== 'month') return false;
                const day = new Date(date);
                day.setHours(0, 0, 0, 0);
                return day.getTime() < today.getTime();
              }}
              tileClassName={({date, view}) => {
                if (view !== 'month') return undefined;
                const d = new Date(date);
                d.setHours(0, 0, 0, 0);
                const iso = toISODateString(d);
                return plannedDateSet.has(iso) ? 'planned-future' : undefined;
              }}
            />
          </CalendarWrapper>
          {selectedDate && (
            <SelectedDateInfo>
              <Text
                text={formatNonTodayFull(selectedDate).replace(',', '')}
                type="bodyNum"
                align="center"
                color={theme.colors.basic00}
              />
              {plannedDateSet.has(toISODateString(selectedDate)) && (
                <Text
                  text={'На выбранную дату уже есть существующий план'}
                  type="bodyM"
                  align="center"
                  color={theme.colors.basic02}
                />
              )}
            </SelectedDateInfo>
          )}
        </ModalContent>
        <ButtonsWrapper>
          <Button
            fullWidth
            title={
              selectedDate
                ? plannedDateSet.has(toISODateString(selectedDate))
                  ? 'Редактировать план'
                  : 'Добавить план'
                : '...'
            }
            variant="filled"
            disabled={!selectedDate}
            onClick={() => {
              if (!selectedDate || !currentSystem) return;
              const dateISO = toISODateString(selectedDate);
              onCloseRequest();
              navigate(`/systems/${currentSystem.id}/plan?date=${dateISO}`);
            }}
          />
          <Button
            fullWidth
            title="Закрыть"
            onClick={() => {
              onCloseRequest();
            }}
          />
        </ButtonsWrapper>
      </ModalWrapper>
    </ModalWindow>
  );
};

export default AddSystemPlanModal;
